/* istanbul ignore file */
import { AxiosInstance } from 'axios';
import { AuthClient } from '.';
import {
  Tenant,
  TenantDeletionEligibility,
  UpdateWorkspaceRequest,
  UpdateWorkspaceResponse,
} from '../models/TenantModels';
import { AuditHistoryRequest, AuditHistoryResponse } from '../models/EventHistoryModels';
import {
  CaseGrant,
  GetCaseGrantsResponse,
  RevokeCaseGrantRequest,
  UpsertCaseGrantRequest,
} from '../models/CaseManagementModels';
import {
  ConfigureIdpRequest,
  GetIdpsResponse,
  GetRolesResponse,
  IdpResponse,
  SamlProviderConfig,
  ValidateIdpRequest,
  ValidateIdpResponse,
} from '../models/IdpModels';
import {
  CEMutationResponse,
  CreateMspInstanceRequest,
  CreateMspInstanceResponse,
  DownloadableLicense,
  PagingRequest,
  TaniumInstance,
  PermissionsResponse,
  EndpointCountResponse,
  MetricHistory,
  ConsoleAccessStatusEntry,
} from '../types';
import { Permission } from '../models/Permissions';
import { useMockApiData } from '../api/index';
import { mockPermissions } from './mockApi';

export class AuthenticatedClient implements AuthClient {
  apiClient: AxiosInstance;

  constructor(apiClient: AxiosInstance) {
    this.apiClient = apiClient;
  }

  async getTenant(): Promise<Tenant> {
    const { data } = await this.apiClient.get<Tenant>('/v1/tenants/self');
    return Promise.resolve(data);
  }

  async getTenantDeletionEligibility(): Promise<TenantDeletionEligibility> {
    const { data } = await this.apiClient.get<TenantDeletionEligibility>('/v1/tenants/self/deletion-eligibility');
    return Promise.resolve(data);
  }

  async deleteTenantSelf(): Promise<void> {
    await this.apiClient.delete<void>('/v1/tenants/self');
    return Promise.resolve();
  }

  async getTenantEndpointCount(): Promise<EndpointCountResponse> {
    const { data } = await this.apiClient.get<EndpointCountResponse>('/v1/tenants/self/endpoint-count');
    return Promise.resolve(data);
  }

  async getLinkedInstances(): Promise<TaniumInstance[]> {
    const { data } = await this.apiClient.get<TaniumInstance[]>('/v1/instances');
    return Promise.resolve(data);
  }

  async getConsoleAccessStatuses(): Promise<ConsoleAccessStatusEntry[]> {
    const { data } = await this.apiClient.get<ConsoleAccessStatusEntry[]>('/v1/instances/console-access-status');
    return Promise.resolve(data);
  }

  async getDownloadableLicenses(): Promise<DownloadableLicense[]> {
    const { data } = await this.apiClient.get<DownloadableLicense[]>('/v1/licenses');
    return Promise.resolve(data);
  }

  async deleteDownloadableLicense(licenseId: number): Promise<void> {
    await this.apiClient.delete<void>(`/v1/licenses/${licenseId}`);
    return Promise.resolve();
  }

  async getIdps(pagingRequest: PagingRequest): Promise<GetIdpsResponse> {
    const { data } = await this.apiClient.get<GetIdpsResponse>('/v1/idps', { params: { ...pagingRequest } });
    return Promise.resolve(data);
  }

  async getRoles(): Promise<GetRolesResponse> {
    const { data } = await this.apiClient.get<GetRolesResponse>('/v1/idp-roles');
    return data;
  }

  async createIdp(idp: ConfigureIdpRequest): Promise<IdpResponse> {
    const { data } = await this.apiClient.post<IdpResponse>('/v1/idps', idp);
    return Promise.resolve(data);
  }

  async updateIdp(idp: ConfigureIdpRequest): Promise<void> {
    await this.apiClient.put<IdpResponse>(`/v1/idps/${idp.id!}`, idp);
  }

  async deleteIdp(idpId: string): Promise<void> {
    await this.apiClient.delete<void>(`/v1/idps/${idpId}`);
  }

  async validateIdp(validateReq: ValidateIdpRequest): Promise<ValidateIdpResponse> {
    const { data } = await this.apiClient.post<ValidateIdpResponse>(
      '/v1/idp-setup/identity-provider-validation',
      validateReq
    );
    return Promise.resolve(data);
  }

  async getIdpSigningCert(): Promise<string> {
    const { data } = await this.apiClient.get<string>('/v1/idp-setup/service-signing-cert.crt');
    return Promise.resolve(data);
  }

  async getServiceProviderMetadataXml(idpName: string): Promise<string> {
    const { data } = await this.apiClient.get<string>(`/v1/idp-setup/service-provider-metadata.xml?idpName=${idpName}`);
    return Promise.resolve(data);
  }

  async getSamlConfig(idpName: string): Promise<SamlProviderConfig> {
    const { data } = await this.apiClient.get<SamlProviderConfig>('/v1/idp-setup/service-provider-metadata', {
      params: { idpName },
    });
    return Promise.resolve(data);
  }

  async getEventHistory(auditHistoryRequest: AuditHistoryRequest): Promise<AuditHistoryResponse> {
    const { data } = await this.apiClient.get<AuditHistoryResponse>('/v1/event-history', {
      params: {
        ...auditHistoryRequest,
      },
    });
    return Promise.resolve(data);
  }

  async createMspInstance(request: CreateMspInstanceRequest): Promise<CreateMspInstanceResponse> {
    const { data } = await this.apiClient.post<CreateMspInstanceResponse>('/v1/instances/msp', request);
    return Promise.resolve(data);
  }

  async validateDomain(domain: string): Promise<{ isUnique: boolean }> {
    const { data } = await this.apiClient.get<{ isUnique: boolean }>('/v1/instances/validate-domain', {
      params: { domain },
    });
    return Promise.resolve(data);
  }

  async deleteLinkedInstance(instanceId: string): Promise<void> {
    await this.apiClient.delete<void>(`/v1/instances/${instanceId}`);
    return Promise.resolve();
  }

  async removeDeletedInstance(instanceId: string): Promise<void> {
    await this.apiClient.delete<void>(`/v1/instances/${instanceId}/remove`);
    return Promise.resolve();
  }

  async deactivateCE(instanceId: string): Promise<CEMutationResponse> {
    const { data } = await this.apiClient.post<CEMutationResponse>(`/v1/instances/${instanceId}/deactivate`);
    return Promise.resolve(data);
  }

  async reactivateCE(instanceId: string): Promise<CEMutationResponse> {
    const { data } = await this.apiClient.post<CEMutationResponse>(`/v1/instances/${instanceId}/reactivate`);
    return Promise.resolve(data);
  }

  async downloadFile(href: string): Promise<Blob> {
    const { data } = await this.apiClient.get<BlobPart>(href, { responseType: 'blob' });
    return new Blob([data]);
  }

  async getPermissions(): Promise<PermissionsResponse> {
    if (useMockApiData) {
      // eslint-disable-next-line no-promise-executor-return
      await new Promise((r) => setTimeout(r, 1000));
      return Promise.resolve({ permissions: mockPermissions });
    }
    const { data } = await this.apiClient.get<{ permissions: Permission[] }>('/v1/permissions');
    return Promise.resolve(data);
  }

  async postAdminSettings(tenantId: string, adminUsername: string, adminEmail: string): Promise<void> {
    await this.apiClient.post(`/v1/tenants/${tenantId}/admin-settings`, {
      username: adminUsername,
      email: adminEmail,
    });
    return Promise.resolve();
  }

  async getMetricHistory(days: number, metricType: string): Promise<MetricHistory> {
    const { data } = await this.apiClient.get<MetricHistory>(
      `/v1/tenants/self/metric-history`,
      { params: { days, metricType } }
    );
    return data;
  }

  async updateWorkspace(tenantId: string, workspace: string): Promise<UpdateWorkspaceResponse> {
    const updateWorkspaceRequest: UpdateWorkspaceRequest = { workspace };
    const { data } = await this.apiClient.post<UpdateWorkspaceResponse>(
      `/v1/tenants/${tenantId}/update-workspace`,
      updateWorkspaceRequest
    );
    return Promise.resolve(data);
  }

  async getCaseGrants(): Promise<GetCaseGrantsResponse> {
    const { data } = await this.apiClient.get<GetCaseGrantsResponse>('/v1/case-grants');
    return Promise.resolve(data);
  }

  async upsertCaseGrant(request: UpsertCaseGrantRequest): Promise<CaseGrant> {
    const { data } = await this.apiClient.put<CaseGrant>('/v1/case-grants', request);
    return Promise.resolve(data);
  }

  async revokeCaseGrant(request: RevokeCaseGrantRequest): Promise<void> {
    await this.apiClient.delete('/v1/case-grants', { params: request });
    return Promise.resolve();
  }
}
